import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";

// Icons
import { IoShieldCheckmark, IoDownload, IoWarning } from "react-icons/io5";

// Services
import mytherraApi from "services/mytherraApi";
import storageService from "services/storageService";

function BackupRecovery() {
  const [showWarning, setShowWarning] = useState(false);
  const [exportedKey, setExportedKey] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleBackupWallet = async () => {
    setLoading(true);
    setError("");
    try {
      // Get encrypted wallet backup
      const backup = storageService.exportWalletBackup();
      
      if (!backup) {
        setError("No encrypted wallet found. Please set a password first in Wallet Encryption.");
        setLoading(false);
        return;
      }

      // Download as JSON file
      const blob = new Blob([backup], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mytherra-wallet-backup-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (err) {
      setError(err.message || "Backup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExportKeys = () => {
    setShowWarning(true);
    setError("");
  };

  const confirmExportKeys = async () => {
    setLoading(true);
    setError("");
    
    try {
      // Get current private key from session
      const privateKey = storageService.loadSession();
      
      if (privateKey) {
        setExportedKey(privateKey);
        setShowWarning(false);
      } else {
        setError("No active session. Please log in first.");
      }
    } catch (err) {
      setError(err.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const backupJson = e.target.result;
        const success = storageService.importWalletBackup(backupJson);
        
        if (success) {
          alert('✅ Backup restored successfully! Please log in with your password.');
          window.location.href = '/sign-in';
        } else {
          setError("Failed to restore backup. Invalid backup file.");
        }
      } catch (err) {
        setError("Error restoring backup: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" mb={1}>
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="48px"
              height="48px"
              borderRadius="12px"
              sx={{ background: "#00CED1" }}
              mr={2}
            >
              <IoShieldCheckmark size="24px" color="#000000" />
            </VuiBox>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Backup & Recovery
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="button" color="text" ml={7}>
            Backup your wallet and keys
          </VuiTypography>
        </VuiBox>

        {/* Error Message */}
        {error && (
          <VuiBox mb={2} p={2} sx={{ background: "rgba(255, 0, 128, 0.1)", borderRadius: "8px", border: "1px solid #FF0080" }}>
            <VuiBox display="flex" alignItems="center">
              <Icon sx={{ color: "#FF0080", mr: 1 }}>error</Icon>
              <VuiTypography variant="caption" color="error">
                {error}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        )}

        {/* Backup Wallet */}
        <VuiBox mb={3} p={2} sx={{ background: "rgba(0, 206, 209, 0.05)", borderRadius: "8px", border: "1px solid rgba(0, 206, 209, 0.2)" }}>
          <VuiBox display="flex" alignItems="flex-start" mb={2}>
            <Icon sx={{ color: "#00CED1", fontSize: "20px", mr: 1 }}>backup</Icon>
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                Backup Encrypted Wallet
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                Download encrypted wallet backup (requires password first)
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          <VuiButton
            variant="outlined"
            color="white"
            fullWidth
            disabled={loading}
            onClick={handleBackupWallet}
            sx={{
              borderColor: "rgba(0, 206, 209, 0.3)",
              "&:hover": {
                borderColor: "rgba(0, 206, 209, 0.6)",
              },
            }}
          >
            <IoDownload size="18px" style={{ marginRight: "8px" }} />
            {loading ? "Creating..." : "Create Backup"}
          </VuiButton>
        </VuiBox>

        {/* Restore Backup */}
        <VuiBox mb={3} p={2} sx={{ background: "rgba(0, 117, 255, 0.05)", borderRadius: "8px", border: "1px solid rgba(0, 117, 255, 0.2)" }}>
          <VuiBox display="flex" alignItems="flex-start" mb={2}>
            <Icon sx={{ color: "#0075F6", fontSize: "20px", mr: 1 }}>upload</Icon>
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                Restore from Backup
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                Import previously created encrypted backup
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          <VuiButton
            variant="outlined"
            color="info"
            fullWidth
            component="label"
            disabled={loading}
            sx={{
              borderColor: "rgba(0, 117, 255, 0.3)",
              "&:hover": {
                borderColor: "rgba(0, 117, 255, 0.6)",
              },
            }}
          >
            <Icon sx={{ mr: 1 }}>upload_file</Icon>
            Select Backup File
            <input
              type="file"
              accept=".json"
              hidden
              onChange={handleRestoreBackup}
            />
          </VuiButton>
        </VuiBox>

        {/* Export Private Keys */}
        <VuiBox mb={3} p={2} sx={{ background: "rgba(255, 215, 0, 0.05)", borderRadius: "8px", border: "1px solid rgba(255, 215, 0, 0.2)" }}>
          <VuiBox display="flex" alignItems="flex-start" mb={2}>
            <Icon sx={{ color: "#FFD700", fontSize: "20px", mr: 1 }}>vpn_key</Icon>
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                Export Private Keys
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                Export private keys for manual backup
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          <VuiButton
            variant="outlined"
            color="warning"
            fullWidth
            onClick={handleExportKeys}
            sx={{
              borderColor: "rgba(255, 215, 0, 0.3)",
              "&:hover": {
                borderColor: "rgba(255, 215, 0, 0.6)",
              },
            }}
          >
            <Icon sx={{ mr: 1 }}>download</Icon>
            Export Keys
          </VuiButton>
        </VuiBox>

        {/* Warning Dialog */}
        {showWarning && (
          <VuiBox mb={3} p={2} sx={{ background: "rgba(255, 0, 128, 0.1)", borderRadius: "8px", border: "1px solid rgba(255, 0, 128, 0.3)" }}>
            <VuiBox display="flex" alignItems="flex-start" mb={2}>
              <IoWarning size="24px" color="#FF0080" style={{ marginRight: "8px" }} />
              <VuiBox>
                <VuiTypography variant="button" color="error" fontWeight="bold">
                  Security Warning!
                </VuiTypography>
                <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                  Anyone with access to your private keys can control your funds. Never share them with anyone!
                </VuiTypography>
              </VuiBox>
            </VuiBox>
            <VuiBox display="flex" gap={2}>
              <VuiButton
                variant="outlined"
                color="white"
                fullWidth
                onClick={() => setShowWarning(false)}
              >
                Cancel
              </VuiButton>
              <VuiButton
                variant="contained"
                color="error"
                fullWidth
                disabled={loading}
                onClick={confirmExportKeys}
                sx={{
                  background: "linear-gradient(135deg, #FF0080 0%, #FF6347 100%)",
                }}
              >
                {loading ? "Exporting..." : "I Understand, Export"}
              </VuiButton>
            </VuiBox>
          </VuiBox>
        )}

        {/* Exported Private Key */}
        {exportedKey && (
          <VuiBox mb={3} p={2} sx={{ background: "rgba(255, 215, 0, 0.1)", borderRadius: "8px", border: "1px solid rgba(255, 215, 0, 0.3)" }}>
            <VuiBox display="flex" alignItems="center" mb={2}>
              <Icon sx={{ color: "#FFD700", mr: 1 }}>vpn_key</Icon>
              <VuiTypography variant="button" color="warning" fontWeight="bold">
                Your Private Key
              </VuiTypography>
            </VuiBox>
            <VuiBox
              p={2}
              sx={{
                background: "rgba(0, 0, 0, 0.3)",
                borderRadius: "8px",
                wordBreak: "break-all",
                fontFamily: "monospace",
                mb: 2
              }}
            >
              <VuiTypography variant="caption" color="white">
                {exportedKey}
              </VuiTypography>
            </VuiBox>
            <VuiBox display="flex" gap={2}>
              <VuiButton
                variant="outlined"
                color="white"
                fullWidth
                onClick={() => {
                  navigator.clipboard.writeText(exportedKey);
                  alert('Private key copied to clipboard!');
                }}
              >
                <Icon sx={{ mr: 1 }}>content_copy</Icon>
                Copy
              </VuiButton>
              <VuiButton
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => setExportedKey("")}
              >
                <Icon sx={{ mr: 1 }}>close</Icon>
                Hide
              </VuiButton>
            </VuiBox>
          </VuiBox>
        )}

        {/* Info Box */}
        <VuiBox p={2} sx={{ background: "rgba(0, 117, 255, 0.1)", borderRadius: "8px" }}>
          <VuiBox display="flex" alignItems="flex-start">
            <Icon sx={{ color: "#0075F6", fontSize: "18px", mr: 1, mt: "2px" }}>info</Icon>
            <VuiBox>
              <VuiTypography variant="caption" color="info" fontWeight="medium">
                Backup Recommendations
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                • Keep backups in multiple secure locations
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                • Encrypt backup files with strong passwords
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                • Test your backups regularly
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default BackupRecovery;
